/**
 * White Label Configuration Utilities
 * Helper functions for white label branding, resolved from the request hostname.
 */

import React from 'react';
import { getCurrentWhiteLabelConfig, isWhiteLabeled } from 'config/whiteLabelDomains';

export const isWhiteLabelEnabled = (): boolean => {
  return isWhiteLabeled();
};

export const getWhiteLabelCompanyName = (): string => {
  const config = getCurrentWhiteLabelConfig();
  return config?.companyName || 'Thera';
};

/** The assistant's name, e.g. "Ask Thera", or the partner's name on a white-label domain. */
export const getWhiteLabelAskLabel = (): string => `Ask ${getWhiteLabelCompanyName()}`;

export const getWhiteLabelLogoUrl = (): string => {
  const config = getCurrentWhiteLabelConfig();
  return config?.logoUrl || process.env.NEXT_PUBLIC_LOGO_URL || '/logo/thera-logo-new.svg';
};

export const getWhiteLabelLogoWithNameUrl = (): string => {
  const config = getCurrentWhiteLabelConfig();
  return config?.logoWithNameUrl || process.env.NEXT_PUBLIC_LOGO_URL || '/logo/thera-logo-new.svg';
};

export const getWhiteLabelWelcomePageTitle = (): string => {
  const companyName = getWhiteLabelCompanyName();
  return `Welcome to ${companyName} Payroll`;
};

export const getWhiteLabelLoaderComponent = (): React.FC | null => {
  const config = getCurrentWhiteLabelConfig();
  return config?.LoaderComponent || null;
};

export const getWhiteLabelFavicons = (): { favicon16: string; favicon32: string } | null => {
  const config = getCurrentWhiteLabelConfig();
  return config?.favicons || null;
};

export const getWhiteLabelDescription = (): string => {
  const config = getCurrentWhiteLabelConfig();
  if (config?.description) {
    return config.description;
  }

  const companyName = getWhiteLabelCompanyName();
  return `Hire, pay, and manage your distributed workforce from ${companyName}'s HR platform.`;
};

export const getWhiteLabelPageTitle = (pageTitle: string): string => {
  const companyName = getWhiteLabelCompanyName();
  return pageTitle.replace(/\bThera\b/g, companyName);
};

export const getWhiteLabelTeamTerm = (): string => {
  const config = getCurrentWhiteLabelConfig();
  return config?.teamTerm || 'Team';
};

export const getWhiteLabelTeamTermPlural = (): string => {
  const teamTerm = getWhiteLabelTeamTerm();
  if (teamTerm === 'Company') return 'Companies';
  if (teamTerm === 'Team') return 'Teams';
  // Default pluralization for other terms
  return teamTerm.endsWith('y') ? teamTerm.slice(0, -1) + 'ies' : teamTerm + 's';
};
